'use client';
// TODO: Fix React 19 compiler lint errors. See https://react.dev/learn/react-compiler
/* eslint-disable react-hooks/preserve-manual-memoization, react-hooks/set-state-in-effect */

import { HeroOverlapType } from '@lib/sanity/queries/modules/heroOverlap/types';
import Anchor from '@ui/Anchor/Anchor';
import { Button } from '@ui/Button/Button';
import cn from '@utils/cn';
import { getUrlWithTrailingSlash } from '@utils/urls';
import React, { useEffect, useMemo, useRef, useState } from 'react';

type HeroTextBoxProps = {
  data: NonNullable<NonNullable<HeroOverlapType['hero']>['heroTextBox']>;
  className?: string;
};

export default function HeroTextBox({ data, className }: HeroTextBoxProps) {
  const [inputValue, setInputValue] = useState('');
  const [displayedText, setDisplayedText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const [isInView, setIsInView] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Animation state in a single ref object
  const animationRef = useRef({
    currentCharIndex: 0,
    currentStringIndex: 0,
    timeoutId: null as NodeJS.Timeout | null,
    isComplete: false,
  });

  const charDelay = (data?.animationDuration || 80) / 1000;
  const showAnimation = !inputValue && !isFocused;
  const staticText = data?.staticPart?.value || '';
  const animatedTextList = useMemo(() => data?.animatedPartList || [], [data?.animatedPartList]);

  // Intersection Observer to detect when component is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsInView(entry.isIntersecting);
        });
      },
      { threshold: 0.3 }, // Trigger when 30% of component is visible
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // Helper functions for cleaner animation logic
  const clearAnimation = () => {
    if (animationRef.current.timeoutId) {
      clearTimeout(animationRef.current.timeoutId);
      animationRef.current.timeoutId = null;
    }
  };

  const resetAnimation = () => {
    animationRef.current.currentCharIndex = 0;
    animationRef.current.currentStringIndex = 0;
    animationRef.current.isComplete = false;
    setDisplayedText('');
  };

  const scheduleNextStep = (callback: () => void, delay: number) => {
    animationRef.current.timeoutId = setTimeout(callback, delay);
  };

  // Main typing animation with pause/resume functionality
  useEffect(() => {
    if (!showAnimation || !animatedTextList.length) {
      clearAnimation();
      resetAnimation();
      return;
    }

    const typeNextCharacter = () => {
      const { currentCharIndex, currentStringIndex } = animationRef.current;
      const currentText = animatedTextList[currentStringIndex];

      if (currentCharIndex < currentText.length) {
        // Type next character
        setDisplayedText(currentText.slice(0, currentCharIndex + 1));
        animationRef.current.currentCharIndex++;
        scheduleNextStep(typeNextCharacter, charDelay * 1000);
      } else {
        // Current string complete - pause then move to next string
        animationRef.current.isComplete = true;
        scheduleNextStep(() => {
          // Clear text and move to next string
          setDisplayedText('');
          animationRef.current.currentCharIndex = 0;
          animationRef.current.currentStringIndex = (currentStringIndex + 1) % animatedTextList.length;
          animationRef.current.isComplete = false;

          // Start typing next string after a brief pause
          scheduleNextStep(typeNextCharacter, 300);
        }, 2000);
      }
    };

    if (isInView) {
      // Resume or start animation
      typeNextCharacter();
    } else {
      // Pause by clearing timeout but keeping state
      clearAnimation();
    }

    return clearAnimation;
  }, [showAnimation, isInView, animatedTextList, charDelay]);

  // Blinking cursor animation
  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 530); // Blink every 530ms

    return () => {
      clearInterval(cursorInterval);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleFocus = () => {
    setIsFocused(true);
    setInputValue(''); // Clear input value when focused
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const getSearchUrl = () => {
    const trimmedValue = inputValue.trim();
    if (!trimmedValue && data?.urlWhenEmpty) {
      return getUrlWithTrailingSlash(data.urlWhenEmpty);
    }
    if (trimmedValue && data?.searchButton?.url) {
      return getUrlWithTrailingSlash(data.searchButton.url.replace('{{search}}', encodeURIComponent(trimmedValue)));
    }
    return '';
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') {
      return;
    }
    event.preventDefault();
    const searchUrl = getSearchUrl();
    if (searchUrl) {
      window.location.href = searchUrl;
    }
  };

  return (
    <div ref={containerRef} className={cn('relative z-20 mx-auto w-full', className)}>
      <div
        className={cn(
          'flex items-stretch gap-0',
          'bg-[rgba(255,255,255,1)] backdrop-blur-sm',
          'rounded-full border border-gray-200/80',
          'shadow-xl shadow-black/10',
          'hover:shadow-2xl hover:shadow-black/15',
          'transition-all duration-300 ease-out',
          'overflow-hidden p-2',
        )}
      >
        {/* Text input area with animation */}
        <div className="relative flex grow">
          {/* Animated placeholder text */}
          {showAnimation && (
            <div className="pointer-events-none absolute inset-0 flex items-center px-6 text-left md:px-11">
              <span className="font-the-future text-xs font-medium text-[rgba(0,0,0,0.5)] md:text-lg">
                {staticText}
                {displayedText}
                <span
                  className={cn(
                    'ml-0.5 border-l-2 border-[rgb(202,89,54)]',
                    showCursor ? 'opacity-100' : 'opacity-0',
                    'transition-opacity duration-75',
                  )}
                >
                  &nbsp;
                </span>
              </span>
            </div>
          )}

          {/* Transparent input overlay */}
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className={cn(
              'w-full px-6 md:px-11',
              'bg-transparent',
              'font-the-future text-xs font-medium text-[rgba(0,0,0,0.5)] md:text-lg',
              'focus:outline-hidden',
              'leading-relaxed',
              'h-14 md:h-24',
              'caret-[rgb(202,89,54)]',
            )}
          />
        </div>

        <Anchor
          aria-label={data.searchButton?.text?.value ?? ''}
          href={getSearchUrl()}
          style={
            {
              '--primary-no-alpha': 'rgb(202,89,54)',
              '--border': 'rgb(202,89,54)',
            } as React.CSSProperties
          }
          className="flex items-center justify-center pr-4"
        >
          <Button size="xl" variant="primary" className=" hidden md:flex">
            {data.searchButton?.text?.value}
          </Button>

          <Button size="s" variant="primary" className="md:hidden">
            {data.searchButton?.text?.value}
          </Button>
        </Anchor>
      </div>
    </div>
  );
}
